import { AssertionDescriptorUsageTypesEnum } from '../../utilities/Constants';
import EntityAssertionControl from '../shared/EntityAssertionControl';
import { IEntityAssertionModel } from '../../interfaces/IAssertionDescriptor';
import { IStrategyPrincipleModel } from '../../interfaces/IStrategy';
import { useZoneGetAssertionDescriptors } from '../../shared/react-query-hooks/useZoneGetAssertionDescriptors';

export interface IStrategyTopicsProps {
    strategyPrinciples: IStrategyPrincipleModel[];
    isEditMode: boolean;
    narrativeFieldsAsPopups: boolean;
    setStrategyPrinciples: (strategyPrinciples: IStrategyPrincipleModel[]) => void;
    setNarrativeField: (params: {
        entityAssertion: IEntityAssertionModel | null;
        callback?: (entityAssertion: IEntityAssertionModel) => void;
    }) => void;
    topicLabel?: string;
    guidanceLabel?: string;
}

export function StrategyTopics({
    guidanceLabel = 'Guidance',
    isEditMode,
    narrativeFieldsAsPopups,
    setNarrativeField,
    setStrategyPrinciples,
    strategyPrinciples,
    topicLabel = 'Topic',
}: IStrategyTopicsProps) {
    const assertionDescriptorsLookup = useZoneGetAssertionDescriptors(
        AssertionDescriptorUsageTypesEnum.Strategy
    );

    const handleTopicChange = (idx: number, value: IEntityAssertionModel[]) => {
        if (!value) return;

        const existing = strategyPrinciples[idx];
        existing.topic = { ...value[0] };

        setStrategyPrinciples([...strategyPrinciples]);
    };

    const handleOpinionChange = (idx: number, value: IEntityAssertionModel[]) => {
        if (!value) return;

        const existing = strategyPrinciples[idx];
        existing.guidance = { ...value[0] };

        setStrategyPrinciples([...strategyPrinciples]);
    };

    if (assertionDescriptorsLookup.isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <table className="table">
            <thead>
                <tr>
                    <th className="col-sm-3">{topicLabel}</th>
                    <th className="col-sm-9">{guidanceLabel}</th>
                </tr>
            </thead>
            <tbody>
                {strategyPrinciples?.map((item, idx) => {
                    const topicAD = assertionDescriptorsLookup.data?.find(
                        (ad) => ad.guid === item.topic?.assertionDescriptorGuid
                    );

                    const guidanceAD = assertionDescriptorsLookup.data?.find(
                        (ad) => ad.guid === item.guidance?.assertionDescriptorGuid
                    );

                    return (
                        <tr key={idx}>
                            <td className="col-sm-3 align-top">
                                {topicAD && (
                                    <EntityAssertionControl
                                        assertionDescriptor={topicAD}
                                        assertionDescriptorUsageType={
                                            AssertionDescriptorUsageTypesEnum.Strategy
                                        }
                                        entityAssertions={[item.topic]}
                                        editMode={isEditMode}
                                        refreshChange={(e: IEntityAssertionModel[]) =>
                                            handleTopicChange(idx, e)
                                        }
                                        narrativeFieldAsPopup={narrativeFieldsAsPopups}
                                        handleNarrativeFieldEdit={(entityAssertion) =>
                                            setNarrativeField({
                                                entityAssertion,
                                                callback: (newEntityAssertion) => {
                                                    handleTopicChange(idx, [newEntityAssertion]);
                                                },
                                            })
                                        }
                                    />
                                )}
                            </td>
                            <td className="col-sm-9 align-top">
                                {guidanceAD && (
                                    <EntityAssertionControl
                                        assertionDescriptor={guidanceAD}
                                        assertionDescriptorUsageType={
                                            AssertionDescriptorUsageTypesEnum.Strategy
                                        }
                                        entityAssertions={[item.guidance]}
                                        editMode={isEditMode}
                                        refreshChange={(e: IEntityAssertionModel[]) =>
                                            handleOpinionChange(idx, e)
                                        }
                                        narrativeFieldAsPopup={narrativeFieldsAsPopups}
                                        handleNarrativeFieldEdit={(entityAssertion) => {
                                            setNarrativeField({
                                                entityAssertion,
                                                callback: (newEntityAssertion) => {
                                                    handleOpinionChange(idx, [newEntityAssertion]);
                                                },
                                            });
                                        }}
                                    />
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}
